import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { post, reportImageUrl, stolenReportsQuery, type ProtecteeStolenReport } from '../api/protectee'
import { ApiError } from '../api/client'
import { dayMonthYear, stamp } from '../inventoree/format'
import { Spinner } from '../ui/Spinner'
import { toast } from '../ui/notify'

const PAGE = 24

/** `moment(x).format('D MMM YYYY, HH:mm')`, ca la restul filelor. */
function fmt(date: string): string {
  const when = stamp(date)
  if (!when) return date
  return `${dayMonthYear(when)}, ${String(when.getHours()).padStart(2, '0')}:${String(when.getMinutes()).padStart(2, '0')}`
}

function mapLink(lat: string, lng: string): string | null {
  const num = /^\s*-?\d+(\.\d+)?\s*$/
  if (!num.test(lat) || !num.test(lng)) return null
  return `https://www.google.com/maps?q=${Number(lat)},${Number(lng)}`
}

function ReportCard({ report, onDelete }: { report: ProtecteeStolenReport; onDelete: (id: number) => void }) {
  const link = mapLink(report.latitude, report.longitude)
  return (
    <div className="col-md-4" style={{ marginBottom: 16 }}>
      <div style={{ border: '1px solid #ddd', borderRadius: 4, padding: 10, height: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <strong>{fmt(report.date)}</strong>
          <button type="button" className="btn btn-xs btn-danger" onClick={() => onDelete(report.id)} title="Delete report">
            <i className="fa fa-trash"></i>
          </button>
        </div>
        <div className="text-muted" style={{ fontSize: 12, margin: '4px 0 8px' }}>
          {report.external_ip ? (
            <span>
              <i className="fa fa-globe"></i> {report.external_ip}
              {'  '}
            </span>
          ) : null}
          {link ? (
            <a href={link} target="_blank" rel="noopener noreferrer">
              <i className="fa fa-map-marker"></i> {report.latitude}, {report.longitude}
            </a>
          ) : null}
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          {report.has_webcam ? (
            <a href={reportImageUrl(report.id, 'webcam')} target="_blank" rel="noopener noreferrer" style={{ flex: 1 }} title="Webcam">
              <img src={reportImageUrl(report.id, 'webcam')} alt="Webcam" style={{ width: '100%', border: '1px solid #eee' }} loading="lazy" />
            </a>
          ) : null}
          {report.has_screenshot ? (
            <a href={reportImageUrl(report.id, 'screenshot')} target="_blank" rel="noopener noreferrer" style={{ flex: 1 }} title="Screenshot">
              <img src={reportImageUrl(report.id, 'screenshot')} alt="Screenshot" style={{ width: '100%', border: '1px solid #eee' }} loading="lazy" />
            </a>
          ) : null}
          {!report.has_webcam && !report.has_screenshot ? (
            <span className="text-muted" style={{ fontSize: 12 }}>No images in this report.</span>
          ) : null}
        </div>
        {report.windows.length ? (
          <details style={{ marginTop: 8 }}>
            <summary style={{ cursor: 'pointer', fontSize: 12 }}>Open windows ({report.windows.length})</summary>
            <ul style={{ margin: '6px 0 0', paddingLeft: 18, fontSize: 12, maxHeight: 140, overflow: 'auto' }}>
              {report.windows.map((w, i) => (
                <li key={i}>{w}</li>
              ))}
            </ul>
          </details>
        ) : null}
      </div>
    </div>
  )
}

/**
 * Fila "Stolen reports": dovezile salvate in portal (poze webcam/ecran, lista
 * ferestrelor, locatia), ca sa nu fim limitati la email. Pozele se incarca pe
 * rand din endpoint-ul autentificat; stergere per raport sau tot.
 */
export function StolenReports({ id }: { id: number }) {
  const client = useQueryClient()
  const [start, setStart] = useState(0)
  const [busy, setBusy] = useState(false)
  const q = useQuery(stolenReportsQuery(id, start, PAGE))

  const del = async (path: string) => {
    if (busy) return
    setBusy(true)
    try {
      const body = await post(path, {})
      toast(body.message, body.status === 'OK' ? 'Success' : 'Error', body.status === 'OK' ? 'success' : 'error')
      void client.invalidateQueries({ queryKey: ['protectee', 'device', id, 'reports'] })
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Something went wrong.', 'Error', 'error')
    } finally {
      setBusy(false)
    }
  }

  if (q.error) return <div className="alert alert-danger">Something went wrong: {(q.error as Error).message}</div>
  if (!q.data) return <Spinner />

  const { reports, total } = q.data
  if (total === 0) {
    return (
      <div className="row">
        <div className="col-md-12">
          <p className="text-muted" style={{ padding: 12 }}>
            No stolen reports yet. While a device is marked stolen, each report (webcam, screenshot, open windows, location) is saved here.
          </p>
        </div>
      </div>
    )
  }

  const from = start + 1
  const to = Math.min(start + PAGE, total)
  return (
    <div className="row">
      <div className="col-md-12">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '4px 0 12px' }}>
          <span className="text-muted">
            {from}&ndash;{to} of {total}
          </span>
          <button
            type="button"
            className="btn btn-sm btn-default"
            disabled={busy}
            onClick={() => {
              if (window.confirm('Delete ALL stored reports for this device?')) void del(`/protectee/devices/${id}/reports/delete-all`)
            }}
          >
            <i className="fa fa-trash"></i> Delete all
          </button>
        </div>
        <div className="row">
          {reports.map((r) => (
            <ReportCard
              key={r.id}
              report={r}
              onDelete={(rid) => {
                if (window.confirm('Delete this report?')) void del(`/protectee/reports/${rid}/delete`)
              }}
            />
          ))}
        </div>
        <div style={{ textAlign: 'center', marginTop: 8 }}>
          <button type="button" className="btn btn-sm btn-default" disabled={start === 0 || busy} onClick={() => setStart(Math.max(0, start - PAGE))}>
            Previous
          </button>{' '}
          <button type="button" className="btn btn-sm btn-default" disabled={to >= total || busy} onClick={() => setStart(start + PAGE)}>
            Next
          </button>
        </div>
      </div>
    </div>
  )
}
